import React, { useState, useEffect, useMemo } from 'react';
import {
  FaTimes,
  FaUserTie,
  FaBuilding,
  FaSearch,
  FaChevronDown,
  FaChevronUp,
  FaUsers,
  FaMapMarkerAlt,
  FaExclamationTriangle,
  FaDownload,
  FaUserSlash,
  FaInfoCircle,
} from 'react-icons/fa';
import { collection, getDocs, doc, updateDoc, deleteField } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { useTheme } from '../../contexts/ThemeContext';
import styles from './SMOOverviewModal.module.css';

const getInitials = (name = '', surname = '') => {
  const a = (name || '').trim().charAt(0).toUpperCase();
  const b = (surname || '').trim().charAt(0).toUpperCase();
  return `${a}${b}` || '?';
};

const SMOOverviewModal = ({ isOpen, onClose, centers, employeesMap, onCenterUpdated }) => {
  const { isDarkMode } = useTheme();
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});
  const [workerCounts, setWorkerCounts] = useState({}); // centerId -> count
  const [centerEmployees, setCenterEmployees] = useState({}); // centerId -> [emp...]
  const [loadingWorkers, setLoadingWorkers] = useState(false);
  const [expandedCenters, setExpandedCenters] = useState({}); // centerId -> bool
  const [confirmRemove, setConfirmRemove] = useState(null); // { centerId, centerName, smoName }
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const fetchWorkerCounts = async () => {
      try {
        setLoadingWorkers(true);
        const snap = await getDocs(collection(db, 'Projects'));
        const counts = {};
        const byCenter = {};
        snap.docs.forEach((d) => {
          const data = d.data();
          const employees = data.employees || [];
          const projectTitle = data.titleShort || data.titleLong || '';
          employees.forEach((emp) => {
            if (!emp.centerId) return;
            counts[emp.centerId] = (counts[emp.centerId] || 0) + 1;
            if (!byCenter[emp.centerId]) byCenter[emp.centerId] = [];
            byCenter[emp.centerId].push({
              id: emp.id,
              name: emp.name || '',
              surname: emp.surname || '',
              role: emp.role || '',
              workerType: emp.workerType || '',
              blindStatus: emp.blindStatus || '',
              status: emp.status || 'active',
              projectId: d.id,
              projectTitle,
            });
          });
        });
        // Sort employees alphabetically per center
        Object.keys(byCenter).forEach((cid) => {
          byCenter[cid].sort((a, b) =>
            `${a.name} ${a.surname}`.localeCompare(`${b.name} ${b.surname}`, 'tr')
          );
        });
        setWorkerCounts(counts);
        setCenterEmployees(byCenter);
      } catch (err) {
        console.error('Error fetching worker counts:', err);
      } finally {
        setLoadingWorkers(false);
      }
    };
    fetchWorkerCounts();
  }, [isOpen]);

  const toggleCenter = (centerId) => {
    setExpandedCenters((prev) => ({ ...prev, [centerId]: !prev[centerId] }));
  };

  // Group centers by SMO
  const { smoGroups, centersWithoutSmo } = useMemo(() => {
    const groups = {};
    const orphans = [];
    (centers || []).forEach((c) => {
      const smoId = c.SMO;
      if (!smoId) {
        orphans.push(c);
        return;
      }
      if (!groups[smoId]) {
        const fullName = employeesMap[smoId] || smoId;
        const parts = fullName.split(' ');
        groups[smoId] = {
          id: smoId,
          fullName,
          name: parts[0] || '',
          surname: parts.slice(1).join(' ') || '',
          centers: [],
        };
      }
      groups[smoId].centers.push(c);
    });

    const list = Object.values(groups).sort((a, b) =>
      a.fullName.localeCompare(b.fullName, 'tr')
    );
    return { smoGroups: list, centersWithoutSmo: orphans };
  }, [centers, employeesMap]);

  const filteredSmoGroups = useMemo(() => {
    if (!search.trim()) return smoGroups;
    const q = search.toLowerCase();
    return smoGroups.filter((g) => {
      if (g.fullName.toLowerCase().includes(q)) return true;
      return g.centers.some(
        (c) =>
          (c['Adı'] && c['Adı'].toLowerCase().includes(q)) ||
          (c['İl'] && c['İl'].toLowerCase().includes(q)) ||
          (c['İlçe'] && c['İlçe'].toLowerCase().includes(q))
      );
    });
  }, [smoGroups, search]);

  const stats = useMemo(() => {
    const totalSmoCenters = smoGroups.reduce((acc, g) => acc + g.centers.length, 0);
    const activeSmoCenters = smoGroups.reduce(
      (acc, g) => acc + g.centers.filter((c) => c['Durum'] === 'Aktif').length,
      0
    );
    const avg =
      smoGroups.length > 0
        ? (totalSmoCenters / smoGroups.length).toFixed(1)
        : '0';
    return {
      smoCount: smoGroups.length,
      totalSmoCenters,
      activeSmoCenters,
      orphanCount: centersWithoutSmo.length,
      avg,
    };
  }, [smoGroups, centersWithoutSmo]);

  const toggleExpand = (smoId) => {
    setExpanded((prev) => ({ ...prev, [smoId]: !prev[smoId] }));
  };

  const expandAll = () => {
    const all = {};
    filteredSmoGroups.forEach((g) => {
      all[g.id] = true;
    });
    setExpanded(all);
  };

  const collapseAll = () => setExpanded({});

  const exportExcel = () => {
    try {
      const rows = [];
      smoGroups.forEach((g) => {
        g.centers.forEach((c) => {
          rows.push({
            'SMO Adı': g.fullName,
            'Merkez Adı': c['Adı'] || '',
            'İl': c['İl'] || '',
            'İlçe': c['İlçe'] || '',
            'Durum': c['Durum'] || '',
            'Çalışan Sayısı': workerCounts[c.id] || 0,
            'Adres': c['Adres'] || '',
          });
        });
      });
      centersWithoutSmo.forEach((c) => {
        rows.push({
          'SMO Adı': '— Atanmamış —',
          'Merkez Adı': c['Adı'] || '',
          'İl': c['İl'] || '',
          'İlçe': c['İlçe'] || '',
          'Durum': c['Durum'] || '',
          'Çalışan Sayısı': workerCounts[c.id] || 0,
          'Adres': c['Adres'] || '',
        });
      });
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'SMO Genel Bakış');
      XLSX.writeFile(wb, 'SMO_GenelBakis.xlsx');
      toast.success('Excel dosyası indirildi');
    } catch (err) {
      console.error(err);
      toast.error('Excel indirilemedi');
    }
  };

  // Remove SMO assignment from a single center.
  // 1) Clears `SMO` field on the Center doc.
  // 2) Clears `smoId` + `centerDeletedAt` on every Projects.employees[] entry
  //    referencing this center, so the SMO Lead Reports fallback no longer
  //    resurrects the assignment. Preserves all Visits / CalendarJobs data.
  const handleConfirmRemove = async () => {
    if (!confirmRemove) return;
    const { centerId } = confirmRemove;
    setRemoving(true);
    try {
      await updateDoc(doc(db, 'Centers', centerId), { SMO: deleteField() });

      const projectsSnap = await getDocs(collection(db, 'Projects'));
      let updatedProjects = 0;
      let updatedEmployees = 0;
      for (const projectDoc of projectsSnap.docs) {
        const data = projectDoc.data();
        const employees = data.employees || [];
        let hasChanges = false;
        const next = employees.map((emp) => {
          if (emp.centerId === centerId && (emp.smoId || emp.centerDeletedAt)) {
            hasChanges = true;
            updatedEmployees++;
            const { smoId, centerDeletedAt, ...rest } = emp;
            return rest;
          }
          return emp;
        });
        if (hasChanges) {
          await updateDoc(doc(db, 'Projects', projectDoc.id), { employees: next });
          updatedProjects++;
        }
      }

      // Update local view immediately
      setWorkerCounts((prev) => prev); // no-op, kept for clarity
      toast.success(
        updatedEmployees > 0
          ? `SMO ataması kaldırıldı. ${updatedProjects} projede ${updatedEmployees} kayıt temizlendi.`
          : 'SMO ataması kaldırıldı.'
      );
      setConfirmRemove(null);
      if (onCenterUpdated) onCenterUpdated();
    } catch (err) {
      console.error('Error removing SMO from center:', err);
      toast.error('SMO kaldırılırken bir hata oluştu');
    } finally {
      setRemoving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`${styles.backdrop} ${isDarkMode ? styles.dark : ''}`} onClick={onClose}>
      <div
        className={`${styles.modal} ${isDarkMode ? styles.dark : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.headerIconWrap}>
              <FaUserTie />
            </div>
            <div>
              <h2 className={styles.title}>SMO Genel Bakış</h2>
              <p className={styles.subtitle}>
                Hangi çalışanın hangi merkezde SMO olduğunu görüntüleyin
              </p>
            </div>
          </div>
          <button className={styles.closeBtn} onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className={styles.statsRow}>
          <div className={styles.statCard}>
            <FaUserTie className={styles.statIcon} />
            <div>
              <div className={styles.statValue}>{stats.smoCount}</div>
              <div className={styles.statLabel}>Toplam SMO</div>
            </div>
          </div>
          <div className={styles.statCard}>
            <FaBuilding className={styles.statIcon} />
            <div>
              <div className={styles.statValue}>{stats.totalSmoCenters}</div>
              <div className={styles.statLabel}>SMO'lu Merkez</div>
            </div>
          </div>
          <div className={`${styles.statCard} ${styles.statActive}`}>
            <FaBuilding className={styles.statIcon} />
            <div>
              <div className={styles.statValue}>{stats.activeSmoCenters}</div>
              <div className={styles.statLabel}>Aktif Merkez</div>
            </div>
          </div>
          <div className={`${styles.statCard} ${styles.statWarn}`}>
            <FaExclamationTriangle className={styles.statIcon} />
            <div>
              <div className={styles.statValue}>{stats.orphanCount}</div>
              <div className={styles.statLabel}>SMO'suz Merkez</div>
            </div>
          </div>
          <div className={styles.statCard}>
            <FaUsers className={styles.statIcon} />
            <div>
              <div className={styles.statValue}>{stats.avg}</div>
              <div className={styles.statLabel}>Ort. Merkez / SMO</div>
            </div>
          </div>
        </div>

        <div className={styles.toolbar}>
          <div className={styles.searchWrap}>
            <FaSearch className={styles.searchIcon} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder="SMO adı, merkez, il veya ilçe ara..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button className={styles.clearSearch} onClick={() => setSearch('')}>
                <FaTimes />
              </button>
            )}
          </div>
          <div className={styles.toolbarActions}>
            <button className={styles.linkBtn} onClick={expandAll}>
              Tümünü Aç
            </button>
            <button className={styles.linkBtn} onClick={collapseAll}>
              Tümünü Kapat
            </button>
            <button className={styles.exportBtn} onClick={exportExcel}>
              <FaDownload /> Excel
            </button>
          </div>
        </div>

        <div className={styles.body}>
          {filteredSmoGroups.length === 0 && centersWithoutSmo.length === 0 && (
            <div className={styles.empty}>
              <FaUserTie className={styles.emptyIcon} />
              <p>Sonuç bulunamadı</p>
            </div>
          )}

          {filteredSmoGroups.map((g) => {
            const isOpen = !!expanded[g.id];
            const active = g.centers.filter((c) => c['Durum'] === 'Aktif').length;
            const inactive = g.centers.length - active;
            const totalWorkers = g.centers.reduce(
              (acc, c) => acc + (workerCounts[c.id] || 0),
              0
            );
            return (
              <div key={g.id} className={styles.smoCard}>
                <button
                  className={styles.smoHeader}
                  onClick={() => toggleExpand(g.id)}
                >
                  <div className={styles.avatar}>
                    {getInitials(g.name, g.surname)}
                  </div>
                  <div className={styles.smoInfo}>
                    <div className={styles.smoName}>{g.fullName}</div>
                    <div className={styles.smoMeta}>
                      <span className={styles.metaPill}>
                        <FaBuilding /> {g.centers.length} merkez
                      </span>
                      <span className={`${styles.metaPill} ${styles.activePill}`}>
                        {active} aktif
                      </span>
                      {inactive > 0 && (
                        <span className={`${styles.metaPill} ${styles.inactivePill}`}>
                          {inactive} inaktif
                        </span>
                      )}
                      <span className={styles.metaPill}>
                        <FaUsers />{' '}
                        {loadingWorkers ? '...' : `${totalWorkers} çalışan`}
                      </span>
                    </div>
                  </div>
                  <div className={styles.chevron}>
                    {isOpen ? <FaChevronUp /> : <FaChevronDown />}
                  </div>
                </button>

                {isOpen && (
                  <div className={styles.centerList}>
                    {g.centers.map((c) => {
                      const emps = centerEmployees[c.id] || [];
                      const isCenterOpen = !!expandedCenters[c.id];
                      const hasEmps = emps.length > 0;
                      return (
                        <div key={c.id} className={styles.centerWrap}>
                          <div
                            className={`${styles.centerRow} ${
                              hasEmps ? styles.centerRowClickable : ''
                            }`}
                            onClick={() => hasEmps && toggleCenter(c.id)}
                          >
                            <div className={styles.centerMain}>
                              <FaBuilding className={styles.centerIcon} />
                              <div>
                                <div className={styles.centerName}>
                                  {c['Adı'] || '—'}
                                </div>
                                <div className={styles.centerLocation}>
                                  <FaMapMarkerAlt />
                                  {[c['İl'], c['İlçe']].filter(Boolean).join(' / ') ||
                                    'Konum belirtilmemiş'}
                                </div>
                              </div>
                            </div>
                            <div className={styles.centerSide}>
                              <span className={styles.workerBadge}>
                                <FaUsers /> {workerCounts[c.id] || 0}
                              </span>
                              <span
                                className={`${styles.statusBadge} ${
                                  c['Durum'] === 'Aktif'
                                    ? styles.statusActive
                                    : styles.statusInactive
                                }`}
                              >
                                {c['Durum'] || '—'}
                              </span>
                              <button
                                type="button"
                                className={styles.removeSmoBtn}
                                title="Bu merkezden SMO atamasını kaldır"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setConfirmRemove({
                                    centerId: c.id,
                                    centerName: c['Adı'] || '—',
                                    smoName: g.fullName,
                                  });
                                }}
                              >
                                <FaUserSlash />
                              </button>
                              {hasEmps && (
                                <span className={styles.centerChevron}>
                                  {isCenterOpen ? <FaChevronUp /> : <FaChevronDown />}
                                </span>
                              )}
                            </div>
                          </div>

                          {isCenterOpen && hasEmps && (
                            <div className={styles.empList}>
                              <div className={styles.empListHeader}>
                                <FaUsers /> Bu merkezde çalışanlar ({emps.length})
                              </div>
                              {emps.map((emp, idx) => (
                                <div
                                  key={`${emp.id}-${emp.projectId}-${idx}`}
                                  className={styles.empRow}
                                >
                                  <div className={styles.empAvatar}>
                                    {getInitials(emp.name, emp.surname)}
                                  </div>
                                  <div className={styles.empInfo}>
                                    <div className={styles.empName}>
                                      {`${emp.name} ${emp.surname}`.trim() || '—'}
                                    </div>
                                    <div className={styles.empMeta}>
                                      {emp.role && (
                                        <span className={styles.empRole}>
                                          {emp.role}
                                        </span>
                                      )}
                                      {emp.projectTitle && (
                                        <span className={styles.empProject}>
                                          {emp.projectTitle}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className={styles.empTags}>
                                    {emp.workerType && (
                                      <span
                                        className={`${styles.empTag} ${
                                          emp.workerType.toLowerCase() === 'primer'
                                            ? styles.tagPrimer
                                            : styles.tagBackup
                                        }`}
                                      >
                                        {emp.workerType}
                                      </span>
                                    )}
                                    {emp.blindStatus && (
                                      <span
                                        className={`${styles.empTag} ${
                                          emp.blindStatus.toLowerCase() === 'blinded'
                                            ? styles.tagBlinded
                                            : styles.tagUnblinded
                                        }`}
                                      >
                                        {emp.blindStatus}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {centersWithoutSmo.length > 0 && !search && (
            <div className={`${styles.smoCard} ${styles.orphanCard}`}>
              <div className={styles.smoHeader} style={{ cursor: 'default' }}>
                <div className={`${styles.avatar} ${styles.orphanAvatar}`}>
                  <FaExclamationTriangle />
                </div>
                <div className={styles.smoInfo}>
                  <div className={styles.smoName}>SMO Atanmamış Merkezler</div>
                  <div className={styles.smoMeta}>
                    <span className={`${styles.metaPill} ${styles.warnPill}`}>
                      {centersWithoutSmo.length} merkez SMO ataması bekliyor
                    </span>
                  </div>
                </div>
              </div>
              <div className={styles.centerList}>
                {centersWithoutSmo.map((c) => (
                  <div key={c.id} className={styles.centerRow}>
                    <div className={styles.centerMain}>
                      <FaBuilding className={styles.centerIcon} />
                      <div>
                        <div className={styles.centerName}>{c['Adı'] || '—'}</div>
                        <div className={styles.centerLocation}>
                          <FaMapMarkerAlt />
                          {[c['İl'], c['İlçe']].filter(Boolean).join(' / ') ||
                            'Konum belirtilmemiş'}
                        </div>
                      </div>
                    </div>
                    <div className={styles.centerSide}>
                      <span
                        className={`${styles.statusBadge} ${
                          c['Durum'] === 'Aktif'
                            ? styles.statusActive
                            : styles.statusInactive
                        }`}
                      >
                        {c['Durum'] || '—'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <button className={styles.cancelBtn} onClick={onClose}>
            Kapat
          </button>
        </div>

        {confirmRemove && (
          <div
            className={styles.confirmBackdrop}
            onClick={() => !removing && setConfirmRemove(null)}
          >
            <div
              className={`${styles.confirmDialog} ${isDarkMode ? styles.dark : ''}`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.confirmHeader}>
                <FaUserSlash className={styles.confirmHeaderIcon} />
                <h3>SMO Atamasını Kaldır</h3>
              </div>
              <div className={styles.confirmBody}>
                <p className={styles.confirmLead}>
                  <strong>{confirmRemove.centerName}</strong> merkezinden{' '}
                  <strong>{confirmRemove.smoName}</strong> SMO atamasını kaldırmak
                  istediğinizden emin misiniz?
                </p>
                <div className={styles.confirmInfo}>
                  <FaInfoCircle className={styles.confirmInfoIcon} />
                  <div>
                    <strong>Ne olacak?</strong>
                    <ul>
                      <li>
                        Bu merkezin SMO ataması temizlenecek ve{' '}
                        <em>SMO Lead Raporları</em> sayfasında bu merkez için SMO
                        görünmeyecek.
                      </li>
                      <li>
                        Merkez, çalışanlar, vizitler, takvim ve{' '}
                        <strong>Manuel Veri Girişi</strong> kayıtları{' '}
                        <strong>korunacaktır</strong>. Sadece SMO ataması kaldırılır.
                      </li>
                      <li>Daha sonra bu merkeze yeni bir SMO atayabilirsiniz.</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className={styles.confirmFooter}>
                <button
                  className={styles.cancelBtn}
                  onClick={() => setConfirmRemove(null)}
                  disabled={removing}
                >
                  İptal
                </button>
                <button
                  className={styles.dangerBtn}
                  onClick={handleConfirmRemove}
                  disabled={removing}
                >
                  {removing ? 'Kaldırılıyor...' : 'SMO Atamasını Kaldır'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SMOOverviewModal;
